import React, { useState } from 'react';
import { Send, CheckCircle2, ExternalLink, ImagePlus, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { SUPPORT } from '../lyphline';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Small on-site warranty claim capture. Logs the claim as a lead in our CRM, then
// directs the customer to Freeze Dryer Support to register / track their claim.
export const WarrantyClaimForm = ({ bg = 'bg-[#F1F5F9]', testid = 'warranty-claim-form' }) => {
  const [f, setF] = useState({ name: '', email: '', phone: '', machine: '', message: '' });
  const [photos, setPhotos] = useState([]); // { url, name }
  const [uploading, setUploading] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if (photos.length + files.length > 6) {
      toast.error('You can attach up to 6 photos.');
      return;
    }
    for (const file of files) {
      if (!file.type.startsWith('image/')) { toast.error('Please choose image files only.'); continue; }
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} is over 10MB.`); continue; }
      setUploading((n) => n + 1);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API_URL}/api/leads/claim-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setPhotos((prev) => [...prev, { url: `${API_URL}${res.data.file_url}`, name: res.data.filename }]);
      } catch (err) {
        toast.error(`Could not upload ${file.name}.`);
      } finally {
        setUploading((n) => n - 1);
      }
    }
  };

  const removePhoto = (url) => setPhotos((prev) => prev.filter((p) => p.url !== url));

  const submit = async () => {
    if (!f.name.trim() || !f.email.trim() || !f.message.trim()) {
      toast.error('Please add your name, email and a short description.');
      return;
    }
    if (uploading > 0) { toast.error('Please wait for photos to finish uploading.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/leads/contact`, {
        name: f.name,
        email: f.email,
        phone: f.phone,
        message: `Warranty claim${f.machine ? ` — Machine: ${f.machine}` : ''}.\n\n${f.message}`,
        form_source: 'warranty-claim',
        symptoms: [],
        photos: photos.map((p) => p.url),
        rush: false,
      });
      setDone(true);
      toast.success("Thanks! We've logged your claim and will be in touch.");
    } catch (e) {
      toast.error('Could not submit your claim. Please try again or use the support portal.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={`py-20 section-padding ${bg}`} data-testid={testid}>
      <div className="max-w-2xl mx-auto">
        <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">Start a claim with us</span>
        <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">Quick warranty claim</h2>
        <p className="text-slate-600 mt-3">
          Give us the basics and we'll get your claim moving. To register your machine or track a claim, finish up at{' '}
          <a href={SUPPORT.url} target="_blank" rel="noopener noreferrer" className="text-[#B87333] font-semibold hover:underline">Freeze Dryer Support</a>.
        </p>
        {done ? (
          <div className="mt-8 bg-white border border-[#CE8946]/40 rounded-2xl p-7" data-testid={`${testid}-success`}>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-[#CE8946] flex-shrink-0" />
              <div>
                <p className="text-[#003456] font-semibold">Claim received — thank you.</p>
                <p className="text-slate-600 text-sm mt-1">Our team will reach out shortly. To register your machine or upload photos and track status, head to Freeze Dryer Support.</p>
                <a href={SUPPORT.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#003456] hover:bg-[#023F63] text-white font-bold px-6 py-3 rounded-md mt-4 transition-colors" data-testid={`${testid}-portal`}>
                  Go to Freeze Dryer Support <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-7 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <input value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="Your name *" className="px-4 py-2.5 rounded-md border bg-white border-slate-300 text-[#003456] text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid={`${testid}-name`} />
              <input value={f.email} onChange={(e) => set('email', e.target.value)} placeholder="Email *" className="px-4 py-2.5 rounded-md border bg-white border-slate-300 text-[#003456] text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid={`${testid}-email`} />
              <input value={f.phone} onChange={(e) => set('phone', e.target.value)} placeholder="Phone" className="px-4 py-2.5 rounded-md border bg-white border-slate-300 text-[#003456] text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid={`${testid}-phone`} />
              <input value={f.machine} onChange={(e) => set('machine', e.target.value)} placeholder="Machine model / serial" className="px-4 py-2.5 rounded-md border bg-white border-slate-300 text-[#003456] text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid={`${testid}-machine`} />
            </div>
            <textarea value={f.message} onChange={(e) => set('message', e.target.value)} rows={4} placeholder="What's happening with your machine? *" className="w-full px-4 py-2.5 rounded-md border bg-white border-slate-300 text-[#003456] text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946] resize-none" data-testid={`${testid}-message`} />

            {/* Photo attachments */}
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-[#003456]">Add photos <span className="text-slate-400 font-normal">(optional, up to 6)</span></label>
                {uploading > 0 && <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…</span>}
              </div>
              <div className="mt-2 flex flex-wrap gap-3">
                {photos.map((p) => (
                  <div key={p.url} className="relative w-20 h-20 rounded-md overflow-hidden border border-slate-200 group" data-testid={`${testid}-photo`}>
                    <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removePhoto(p.url)} className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full p-0.5" aria-label="Remove photo" data-testid={`${testid}-photo-remove`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {photos.length < 6 && (
                  <label className="w-20 h-20 rounded-md border-2 border-dashed border-slate-300 hover:border-[#CE8946] flex flex-col items-center justify-center gap-1 cursor-pointer text-slate-400 hover:text-[#CE8946] transition-colors" data-testid={`${testid}-photo-add`}>
                    <ImagePlus className="h-5 w-5" />
                    <span className="text-[10px] font-medium">Add</span>
                    <input type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} data-testid={`${testid}-photo-input`} />
                  </label>
                )}
              </div>
            </div>

            <button onClick={submit} disabled={submitting || uploading > 0} className="inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-7 py-3 rounded-md transition-colors disabled:opacity-60" data-testid={`${testid}-submit`}>
              {submitting ? 'Submitting…' : (<>Submit claim <Send className="h-4 w-4" /></>)}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default WarrantyClaimForm;
